import { useState } from 'react';
import { ListItemText, TextField, Grid } from '@mui/material';
import {
  CustomCompany,
  CustomDateSpectrum,
  LabeledTextFieldProps,
  CustomDimensionValue,
  ElementReference,
  DimensionValueReference
} from '../../api/customObjectsTypes';
import { ElementLabel } from './elementLabel';
import { EndpointLabel } from './endpointLabel';
import { SearchSelectLabel } from './searchSelectLabel';
import { SelectLabel } from './selectLabel';
import { TagGroupLabel } from './tagGroupLabel';
import { DateSpectrumLabel } from './dateSpectrumLabel';
import { CombinedCheckBoxLabel } from './checkboxLabel';
import { CompanyLabel } from './companyLabel';
import { DimensionLabel } from './dimensionLabel';
import { ParserTypeLabel } from './parserTypeLabel';

const LabeledTextField = ({
  id,
  label,
  fieldType,
  options,
  placeholder,
  value,
  disabled,
  endpointUrl,
  alternative = false,
  showDetailed = true,
  onValueChange,
  urlType,
  includeCustom,
  page = "normal",
  isAlternate,
  dimensionName,
  taxonomyId,
}: LabeledTextFieldProps) => {
  const [selectedValue, setSelectedValue] = useState<
    CustomCompany |
    CustomDateSpectrum |
    CustomDimensionValue |
    ElementReference |
    DimensionValueReference |
    string | null | boolean | number
  >(value || null);

  const renderFieldType = () => {
    switch (fieldType) {
      case "dimension":
        if (isAlternate === "alternateDimension" || isAlternate === "alternateParentMember" || isAlternate === "alternateElement" || isAlternate === "alternateParentMemberDimension") {
          return (
            <DimensionLabel
              taxonomyId={taxonomyId}
              alternateUrl={isAlternate}
              label={label}
              disabled={disabled}
              value={value as DimensionValueReference | undefined}
              onValueChange={onValueChange as ((event: DimensionValueReference) => void)}
              placeholder={placeholder}
              preDefinedOptions={options as DimensionValueReference[]}
              selectedValue={selectedValue as DimensionValueReference}
              setSelectedValue={setSelectedValue}
              includeCustom={includeCustom}
              showDetailed={showDetailed}
              page={page}
              dimensionName={dimensionName}
            />
          );
        }
        return (
          <DimensionLabel
            label={label}
            disabled={disabled}
            value={value as DimensionValueReference | undefined}
            onValueChange={onValueChange as ((event: DimensionValueReference) => void)}
            placeholder={placeholder}
            preDefinedOptions={options as DimensionValueReference[]}
            selectedValue={selectedValue as DimensionValueReference}
            setSelectedValue={setSelectedValue}
            includeCustom={includeCustom}
            showDetailed={showDetailed}
            page={page}
          />
        );
      case "element":
        return (
          <ElementLabel
            label={label}
            disabled={disabled}
            value={value as ElementReference | undefined}
            onValueChange={onValueChange as ((event: ElementReference) => void) | undefined}
            placeholder={placeholder}
            preDefinedOptions={options as ElementReference[]}
            selectedValue={selectedValue as ElementReference}
            setSelectedValue={setSelectedValue}
            includeCustom={includeCustom}
            page={page}
          />
        );
      case "endpoint":
        return (
          <EndpointLabel
            label={label}
            value={value as string}
            disabled={disabled}
            onValueChange={(e) =>
              onValueChange && onValueChange(e)
            }
            placeholder={placeholder}
            setSelectedValue={setSelectedValue}
            endpoint={endpointUrl as string}
            urlType={urlType}
          />
        );
      case "parserDataType":
        return (
          <ParserTypeLabel
            label={label}
            value={value as number}
            disabled={disabled}
            onValueChange={(e) =>
              onValueChange && onValueChange(e)
            }
            placeholder={placeholder}
            setSelectedValue={setSelectedValue}
            endpoint={endpointUrl as string}
          />
        );
      case "searchSelect":
      case "languages":
        return (
          <SearchSelectLabel
            label={label}
            disabled={disabled}
            onValueChange={(e) =>
              onValueChange && onValueChange(e)
            }
            placeholder={placeholder}
            value={value as string}
          />
        );
      case "select":
        return (
          <SelectLabel
            label={label}
            value={value as boolean | null}
            disabled={disabled}
            onValueChange={(e) =>
              onValueChange && onValueChange(e)
            }
            options={options as string[]}
            placeholder={placeholder}
          />
        );
      case "tagGroup":
        return (
          <TagGroupLabel
            label={label}
            disabled={disabled}
            onValueChange={(e) =>
              onValueChange && onValueChange(e)
            }
            placeholder={placeholder}
            value={value as string}
          />
        );
      case "dateSpectrum":
        return (
          <DateSpectrumLabel
            label={label}
            disabled={disabled}
            onValueChange={(e) => {
              onValueChange && onValueChange(e);
            }}
            placeholder={placeholder}
            value={value as string}
            setSelectedValue={setSelectedValue}
          />
        );
      case "checkbox":
        return (
          <CombinedCheckBoxLabel
            label={label}
            onValueChange={(newValue) => onValueChange?.(newValue)}
            value={value as boolean | null}
            disabled={disabled}
            alternative={false}
          />
        );
      case "alternateCheckbox":
        return (
          <CombinedCheckBoxLabel
            label={label}
            disabled={disabled}
            onValueChange={(newValue) => onValueChange?.(newValue)}
            value={value as boolean | null}
            alternative={true}
          />
        );
      case "company":
        return (
          <CompanyLabel
            label={label}
            disabled={disabled}
            onValueChange={
              onValueChange as ((event: React.ChangeEvent<HTMLInputElement>) => void) | undefined
            }
            placeholder={placeholder}
            value={value as string}
            selectedValue={selectedValue as CustomCompany}
            setSelectedValue={setSelectedValue}
          />
        );
      case "text":
        return (
          <Grid container spacing={0} sx={{ mb: 2 }} maxWidth={"false"}>
            <Grid size={2}>
              <ListItemText primary={label} primaryTypographyProps={{ fontWeight: 600, fontSize: 14 }} />
            </Grid>
            <Grid size={10}>
              <TextField
                autoComplete='off'
                fullWidth
                sx={{ width: "100%" }}
                inputProps={{ style: { fontSize: 14 } }}
                InputLabelProps={{ style: { fontSize: 14 } }}
                variant="standard"
                placeholder={placeholder || ""}
                value={value || ""}
                disabled={disabled}
                onChange={(e) =>
                  onValueChange && onValueChange(e.target.value)
                }
              />
            </Grid>
          </Grid>
        );
      default:
        return null;
    }
  };

  return renderFieldType();
};

export default LabeledTextField;